import React, { useState } from 'react';
import { Separator } from '@vaiu/ui/separator';
import { Button } from '@vaiu/ui/button';
import { ChevronUp, ChevronDown } from 'lucide-react';
import VersionInfo from '@/components/shared/VersionInfo';
import WhiteLabelFooter from '@/components/shared/WhiteLabelFooter';
import { resolveTenantBranding } from '@/utils/tenantBranding';

interface FooterProps {
  className?: string;
  showVaiuBranding?: boolean; // For white-label scenarios
}

const Footer: React.FC<FooterProps> = ({ 
  className = '',
  showVaiuBranding = true 
}) => {
  const currentYear = new Date().getFullYear();
  const [isExpanded, setIsExpanded] = useState(false);
  const tenant = resolveTenantBranding();
  const copyrightHolder = tenant.showPoweredByVaiu ? tenant.displayName : 'Vaiu Global Inc.';

  return (
    <footer className={`bg-gray-50 border-t border-gray-200 mt-8 ${className}`}>
      <WhiteLabelFooter />
      <div className="w-full max-w-sm sm:max-w-md lg:max-w-xl mx-auto px-4">
        
        {/* Collapsed State - Always Visible */}
        <div className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-3">
            {showVaiuBranding && (
              <div>
                {/* <VaiuLogo variant="default" size="sm" showTagline={false} /> */}
              </div>
            )}
            <div className="text-sm text-gray-600">
              © {currentYear} {copyrightHolder}. All rights reserved.
            </div>
            <div className="text-xs text-gray-400">
              <VersionInfo format="inline" />
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-600 hover:text-gray-900 self-start sm:self-center"
          >
            <span className="text-xs mr-2">
              {isExpanded ? 'Show Less' : 'Legal & Company Info'}
            </span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </Button>
        </div>

        {/* Expanded State - Detailed Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 pb-6">
            <div className="py-6">
              
              {/* Main Footer Content - Mobile Optimized */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                
                {/* Company Info */}
                <div className="space-y-3 sm:col-span-2">
                  <p className="text-sm text-gray-600">
                    Secure tokenized payment technology for the digital economy.
                  </p>
                </div>

                {/* Legal */}
                <div className="sm:col-span-1">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                    Legal
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li><a href="/terms-of-service" className="hover:text-gray-900 transition-colors">Terms of Service</a></li>
                    <li><a href="/privacy-policy" className="hover:text-gray-900 transition-colors">Privacy Policy</a></li>
                    <li><a href="/cookie-policy" className="hover:text-gray-900 transition-colors">Cookie Policy</a></li>
                    <li><a href="/cookie-settings" className="hover:text-gray-900 transition-colors">Cookie Settings</a></li>
                    <li><a href="/security" className="hover:text-gray-900 transition-colors">Security</a></li>
                    <li><a href="/compliance" className="hover:text-gray-900 transition-colors">Compliance</a></li>
                  </ul>
                </div>

                {/* Support */}
                <div className="sm:col-span-1">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                    Support
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li><a href="/help" className="hover:text-gray-900 transition-colors">Help Center</a></li>
                    <li><a href="/contact" className="hover:text-gray-900 transition-colors">Contact Us</a></li>
                    <li><a href="/api-docs" className="hover:text-gray-900 transition-colors">API Documentation</a></li>
                  </ul>
                </div>

                {/* Company */}
                <div className="sm:col-span-2 lg:col-span-1">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                    Company
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li><a href="/about" className="hover:text-gray-900 transition-colors">About Us</a></li>
                    <li><a href="/careers" className="hover:text-gray-900 transition-colors">Careers</a></li>
                    <li><a href="/press" className="hover:text-gray-900 transition-colors">Press</a></li>
                    <li><a href="/partners" className="hover:text-gray-900 transition-colors">Partners</a></li>
                    <li><a href="/acknowledgements" className="hover:text-gray-900 transition-colors">Acknowledgements</a></li>
                  </ul>
                </div>
              </div>

              <Separator className="my-6" />

              {/* Legal Notices & Copyright - Mobile Optimized */}
              <div className="space-y-4">
                
                {/* Trademark and Patent Notices */}
                <div className="text-xs text-gray-500 space-y-1">
                  <p>
                    <strong>Vaiu™</strong> and <strong>Tokenized Pay™</strong> are trademarks of Vaiu Global Inc. 
                    Patent Pending. All rights reserved.
                  </p>
                  <p>
                    Vaiu® is a registered trademark of Vaiu Global Inc. in the United States and other countries.
                  </p>
                </div>

                {/* Regulatory Notices - Simplified for mobile */}
                <div className="text-xs text-gray-500 space-y-1">
                  <p>
                    <strong>Important:</strong> Vaiu Global Inc. is not a bank. Banking services provided by licensed partners. 
                    Prepaid cards issued by licensed financial institutions.
                  </p>
                  <p>
                    Digital token transactions subject to applicable money transmission and financial services regulations.
                  </p>
                </div>

                {/* Security & Compliance */}
                <div className="text-xs text-gray-500 space-y-2">
                  <p>
                    🔒 Enterprise-grade encryption. PCI DSS Level 1 compliant via our partners.
                  </p>
                  <p>
                    Card processing services are provided by <a href="https://stripe.com/docs/security/stripe" target="_blank" rel="noopener noreferrer" className="underline">Stripe</a>, 
                    and bank verification services by <a href="https://plaid.com/security/" target="_blank" rel="noopener noreferrer" className="underline">Plaid</a>. 
                    Both are certified to the highest industry standards.
                  </p>
                </div>
 
                <Separator className="my-3" />
 
                {/* Final Legal - Mobile Stacked */}
                <div className="flex flex-col space-y-2 lg:flex-row lg:justify-between lg:items-center lg:space-y-0">
                  
                  <div className="text-xs text-gray-500">
                    <p>
                      Vaiu Global Inc. | 8 The Green #19779, Dover, DE 19901 | Delaware Corporation | USA
                    </p>
                  </div>
                </div>

                {/* Build Information */}
                <div className="text-xs text-gray-400 pt-2 border-t border-gray-100">
                  <p>
                    <strong>Build:</strong> <VersionInfo format="inline" showLabel={true} />
                  </p>
                </div>

                {/* Risk Disclaimer */}
                <div className="text-xs text-gray-400 pt-1">
                  <p>
                    <strong>Risk Disclosure:</strong> Digital payment tokens may be subject to market volatility. 
                    Past performance does not guarantee future results. Prepaid cards have fees and limitations. 
                    Please read all terms and conditions carefully before use.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </footer>
  );
};

export default Footer; 